import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ArticleConfigSuggestRequest {
  project_id: string;
  topic?: string;
  title_hint?: string;
  internal_urls?: string[];
  cta_urls?: string[];
}

export interface ArticleConfigSuggestResponse {
  suggested_titles: string[];
  titles_used_by_competitors: string[];
  suggested_keywords: string[];
  suggested_internal_links: { label: string; url: string }[];
  suggested_ctas: { label: string; url: string }[];
  suggested_faqs: { question: string; answer: string }[];
}

export interface ArticleGenerateRequest {
  project_id: string;
  projectdata_id?: string;
  audience_location?: string;
  title: string;
  keywords?: string[];
  word_count?: number;
  tone_of_voice?: string;
  internal_links?: { label: string; url: string }[];
  ctas?: { label: string; url: string }[];
  faqs?: { question: string; answer: string }[];
  with_ai_generated_images?: boolean;
}

export interface ArticleGenerateResponse {
  id?: string;
  title: string;
  content: string;
  title_tag?: string;
  meta_description?: string;
  seo_score?: number;
}

export interface ArticleHistoryItem {
  id: string;
  title: string;
  created_at: string;
  status: string;
}

export interface ArticleHistoryResponse {
  items: ArticleHistoryItem[];
  total: number;
}

@Injectable({
  providedIn: 'root'
})
export class ArticleService {
  private readonly baseUrl = `${environment.apiUrl.replace(/\/$/, '')}/api/v1`;

  constructor(private http: HttpClient) {}

  suggestConfig(request: ArticleConfigSuggestRequest): Observable<ArticleConfigSuggestResponse> {
    return this.http.post<ArticleConfigSuggestResponse>(`${this.baseUrl}/article-config/suggest`, request);
  }

  generate(request: ArticleGenerateRequest): Observable<ArticleGenerateResponse> {
    return this.http.post<ArticleGenerateResponse>(`${this.baseUrl}/article/generate`, request);
  }

  getHistory(projectId: string, skip: number = 0, limit: number = 20): Observable<ArticleHistoryResponse> {
    const params = new HttpParams()
      .set('project_id', projectId)
      .set('skip', skip.toString())
      .set('limit', limit.toString());
    return this.http.get<ArticleHistoryResponse>(`${this.baseUrl}/article/history`, { params });
  }

  getById(articleId: string): Observable<ArticleGenerateResponse> {
    return this.http.get<ArticleGenerateResponse>(`${this.baseUrl}/article/${articleId}`);
  }

  export(articleId: string, format: 'doc' | 'txt' = 'doc'): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/article/${articleId}/export`, {
      params: { format },
      responseType: 'blob'
    });
  }
}
